import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  Checkbox,
  Message,
  Segment,
  Header,
  Divider,
} from 'semantic-ui-react';

interface TaskParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'integer';
  required: boolean;
  description?: string;
  default_value?: any;
  validation_rules?: any;
  ui_hints?: any;
}

interface TaskParameterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRunTask: (parameters: Record<string, any>) => void;
  taskName: string;
  taskDescription: string;
  parameters: TaskParameter[];
  loading?: boolean;
}

const TaskParameterModal: React.FC<TaskParameterModalProps> = ({
  isOpen,
  onClose,
  onRunTask,
  taskName,
  taskDescription,
  parameters,
  loading = false,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data with default values
  useEffect(() => {
    if (isOpen && parameters) {
      const initialData: Record<string, any> = {};
      parameters.forEach((param) => {
        if (param.default_value !== undefined && param.default_value !== null) {
          initialData[param.name] = param.default_value;
        } else if (param.type === 'boolean') {
          initialData[param.name] = false;
        } else if (param.type === 'number' || param.type === 'integer') {
          initialData[param.name] = '';
        } else {
          initialData[param.name] = '';
        }
      });
      setFormData(initialData);
      setErrors({});
    }
  }, [isOpen, parameters]);

  const handleInputChange = (paramName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [paramName]: value
    }));
    
    // Clear error when user starts typing
    if (errors[paramName]) {
      setErrors(prev => ({
        ...prev,
        [paramName]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    parameters.forEach((param) => {
      const value = formData[param.name];
      
      // Check required fields
      if (param.required && (value === undefined || value === null || value === '')) {
        newErrors[param.name] = `${param.name} is required`;
        return;
      }
      
      // Type validation
      if (value !== undefined && value !== null && value !== '') {
        if (param.type === 'number' || param.type === 'integer') {
          const numValue = Number(value);
          if (isNaN(numValue)) {
            newErrors[param.name] = `${param.name} must be a valid number`;
          } else if (param.type === 'integer' && !Number.isInteger(numValue)) {
            newErrors[param.name] = `${param.name} must be a valid integer`;
          }
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Convert form data to proper types
      const processedData: Record<string, any> = {};
      parameters.forEach((param) => {
        const value = formData[param.name];
        if (value !== undefined && value !== null && value !== '') {
          if (param.type === 'number' || param.type === 'integer') {
            processedData[param.name] = Number(value);
          } else if (param.type === 'boolean') {
            processedData[param.name] = Boolean(value);
          } else {
            processedData[param.name] = String(value);
          }
        }
      });
      
      onRunTask(processedData);
    }
  };

  const renderParameterInput = (param: TaskParameter) => {
    const value = formData[param.name];
    const error = errors[param.name];
    
    switch (param.type) {
      case 'boolean':
        return (
          <Form.Field key={param.name}>
            <Checkbox
              label={param.name}
              checked={Boolean(value)}
              onChange={(e, { checked }) => handleInputChange(param.name, checked)}
              error={!!error}
            />
            {param.description && (
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                {param.description}
              </div>
            )}
            {error && <Message size="small" negative>{error}</Message>}
          </Form.Field>
        );
        
      case 'number':
      case 'integer':
        return (
          <Form.Field key={param.name} required={param.required}>
            <label>{param.name}</label>
            <Input
              type="number"
              value={value}
              onChange={(e) => handleInputChange(param.name, e.target.value)}
              placeholder={param.default_value ? String(param.default_value) : `Enter ${param.name}`}
              error={!!error}
            />
            {param.description && (
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                {param.description}
              </div>
            )}
            {error && <Message size="small" negative>{error}</Message>}
          </Form.Field>
        );
        
      case 'string':
      default:
        return (
          <Form.Field key={param.name} required={param.required}>
            <label>{param.name}</label>
            <Input
              value={value}
              onChange={(e) => handleInputChange(param.name, e.target.value)}
              placeholder={param.default_value ? String(param.default_value) : `Enter ${param.name}`}
              error={!!error}
            />
            {param.description && (
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                {param.description}
              </div>
            )}
            {error && <Message size="small" negative>{error}</Message>}
          </Form.Field>
        );
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} size="small">
      <Modal.Header>
        <Header as="h3">
          Run Task: {taskName}
        </Header>
      </Modal.Header>
      
      <Modal.Content>
        <Segment>
          <Header as="h4">Task Description</Header>
          <p>{taskDescription}</p>
        </Segment>
        
        {parameters && parameters.length > 0 ? (
          <>
            <Divider />
            <Header as="h4">Parameters</Header>
            <Form>
              {parameters.map(renderParameterInput)}
            </Form>
          </>
        ) : (
          <Message info>
            This task does not require any parameters.
          </Message>
        )}
      </Modal.Content>
      
      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          primary 
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        >
          Run Task
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TaskParameterModal;
